import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { MapPin, CheckCircle2, MinusCircle } from "lucide-react";

export default function Branches() {
  const [rows, setRows] = useState([]);

  const load = async () => {
    try {
      const { data } = await api.get("/branches");
      setRows(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => { load(); }, []);

  const toggle = async (b) => {
    const prev = rows;
    setRows(rows.map((r) => (r.id === b.id ? { ...r, included: !b.included } : r)));
    try {
      await api.patch(`/branches/${b.id}`, { included: !b.included });
      toast.success(b.included ? "تم استثناء الفرع" : "تم تضمين الفرع");
    } catch (e) {
      setRows(prev);
      toast.error(formatApiError(e));
    }
  };

  const activeCount = rows.filter((r) => r.included).length;

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <div className="p-5 md:p-8 max-w-[1400px] mx-auto space-y-6">
        <header className="flex flex-wrap items-end justify-between gap-4 fade-up">
          <div>
            <p className="text-[11px] tracking-[0.18em] uppercase text-[var(--text-muted)] font-semibold">الفروع</p>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-[var(--text-primary)] mt-1">إدارة الفروع</h1>
            <p className="text-[var(--text-secondary)] mt-1.5 text-sm">
              عطّل أي فرع لا تريد احتساب مخزونه في الإجمالي المرسل لسلة.
            </p>
          </div>
          <div className="flex items-center gap-2 bg-[var(--bg-card)] border border-[var(--border-color)] rounded-xl px-4 py-2.5 text-sm">
            <span className="w-2 h-2 rounded-full bg-emerald-500" />
            <span className="text-[var(--text-secondary)] font-medium">{activeCount} من {rows.length} فرع مُفعّل</span>
          </div>
        </header>

        {rows.length === 0 && (
          <div className="bg-[var(--bg-card)] rounded-2xl border border-[var(--border-color)] py-16 text-center text-sm text-[var(--text-muted)]">
            لا توجد فروع — استورد بيانات قيود أولاً من صفحة الإعدادات.
          </div>
        )}

        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3 stagger">
          {rows.map((b) => (
            <div
              key={b.id}
              data-testid="branch-card"
              className={`bg-[var(--bg-card)] rounded-2xl border p-5 card-hover fade-up transition-all ${
                b.included
                  ? "border-[var(--border-color)]/80 hover:border-[#004EEB]/30"
                  : "border-dashed border-[var(--border-color)] opacity-60"
              }`}
            >
              {/* Top bar accent */}
              <div className={`h-[3px] rounded-full mb-4 ${b.included ? "bg-[#004EEB]" : "bg-slate-200"}`} />

              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1.5">
                    <MapPin className={`w-4 h-4 shrink-0 ${b.included ? "text-[#004EEB]" : "text-[var(--text-muted)]"}`} />
                    <h3 className="text-base font-semibold text-[var(--text-primary)] truncate">{b.name_ar}</h3>
                  </div>
                  <div className="text-[11px] text-[var(--text-muted)] mono mt-1">ID: {b.qoyod_branch_id}</div>
                  <div className="text-[11px] text-[var(--text-muted)] mt-0.5">{b.city}</div>
                </div>
                <Switch
                  data-testid="branch-toggle-switch"
                  checked={b.included}
                  onCheckedChange={() => toggle(b)}
                />
              </div>

              <div className="mt-4 pt-3 border-t border-[var(--border-color)] flex items-center justify-between text-xs">
                <div className={`flex items-center gap-1.5 font-semibold ${b.included ? "text-emerald-600" : "text-[var(--text-muted)]"}`}>
                  {b.included ? (
                    <CheckCircle2 className="w-3.5 h-3.5" />
                  ) : (
                    <MinusCircle className="w-3.5 h-3.5" />
                  )}
                  {b.included ? "مُضمَّن في الإجمالي" : "مُستثنى"}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
